import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search,
  RefreshCw,
  TrendingUp,
  Zap,
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  Activity,
  Users,
  Wrench,
  FileText,
  Calendar,
  Eye,
  ChevronDown,
  ChevronRight,
  Timer,
  Database,
  Bot,
  PlayCircle,
  StopCircle,
  X
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { useExecutionRecordsApi, ExecutionRecord } from '../apis/execution-records-apis';
import { useUsersApi } from '../apis/users-apis';
import { usePlaybooksApi } from '../apis/playbooks-apis';
import { UserData, Playbook } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/Select';
import { Badge } from '../components/ui/Badge';
import { Label } from '../components/ui/Label';

interface ExecutionMetrics {
  totalExecutions: number;
  successRate: number;
  avgDuration: number;
  totalCost: number;
  activeExecutions: number;
  errorRate: number;
  avgTokensPerExecution: number;
  topPlaybooks: { name: string; count: number }[];
}

interface FilterState {
  status: string;
  playbookName: string;
  ownerId: string;
  dateFrom: Date | null;
  dateTo: Date | null;
}

const AllExecutionsPage: React.FC = () => {
  const navigate = useNavigate();
  
  // Core state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [executions, setExecutions] = useState<ExecutionRecord[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(15);
  
  // Supporting data
  const [userMap, setUserMap] = useState<Map<string, string>>(new Map());
  const [playbooks, setPlaybooks] = useState<Playbook[]>([]);
  
  // UI state
  const [viewMode, setViewMode] = useState<'cards' | 'table' | 'metrics'>('metrics');
  const [refreshing, setRefreshing] = useState(false);
  
  // Playbook search state
  const [playbookSearchTerm, setPlaybookSearchTerm] = useState('');
  const [playbookDropdownOpen, setPlaybookDropdownOpen] = useState(false);
  
  // Filter state
  const [filters, setFilters] = useState<FilterState>({
    status: 'all',
    playbookName: 'all',
    ownerId: '',
    dateFrom: null,
    dateTo: null,
  });
  
  const { getExecutionRecords } = useExecutionRecordsApi();
  const { getUsers } = useUsersApi();
  const { getPlaybooks } = usePlaybooksApi();

  // Filtered playbooks based on search term
  const filteredPlaybooks = useMemo(() => {
    if (!playbookSearchTerm) return playbooks;
    return playbooks.filter(playbook => 
      playbook.playbookName.toLowerCase().includes(playbookSearchTerm.toLowerCase())
    );
  }, [playbooks, playbookSearchTerm]);

  // Computed metrics
  const metrics = useMemo((): ExecutionMetrics => {
    const totalExecutions = totalRecords; // Use total records across all pages
    const successfulExecutions = executions.filter(e => e.status?.toLowerCase() === 'success').length;
    const activeExecutions = executions.filter(e => e.status?.toLowerCase() === 'in_progress').length;
    const errorExecutions = executions.filter(e => e.status?.toLowerCase() === 'error').length;
    
    // Calculate rates based on current page data but show total count
    const currentPageCount = executions.length;
    const successRate = currentPageCount > 0 ? (successfulExecutions / currentPageCount) * 100 : 0;
    const errorRate = currentPageCount > 0 ? (errorExecutions / currentPageCount) * 100 : 0;
    
    // Calculate average duration only from executions that have valid duration data
    const executionsWithDuration = executions.filter(e => {
      const duration = typeof e.duration === 'string' ? parseFloat(e.duration) : e.duration;
      return duration && duration > 0 && duration < 86400; // Filter out durations > 24 hours as likely errors
    });
    
    const avgDuration = executionsWithDuration.length > 0 
      ? executionsWithDuration.reduce((sum, e) => {
          const duration = typeof e.duration === 'string' ? parseFloat(e.duration) : e.duration;
          return sum + (duration || 0);
        }, 0) / executionsWithDuration.length
      : 0;
    
    // Debug logging for unusual durations
    if (executions.some(e => {
      const duration = typeof e.duration === 'string' ? parseFloat(e.duration) : e.duration;
      return duration && duration > 86400;
    })) {
      console.warn('Found executions with unusually large durations:', 
        executions.filter(e => {
          const duration = typeof e.duration === 'string' ? parseFloat(e.duration) : e.duration;
          return duration && duration > 86400;
        }).map(e => ({ id: e.executionId, duration: e.duration }))
      );
    }
    const totalCost = executions.reduce((sum, e) => sum + (e.estimatedCost || 0), 0);
    const avgTokensPerExecution = executions.reduce((sum, e) => sum + (e.totalTokens || 0), 0) / currentPageCount || 0;
    
    // Get top playbooks
    const playbookCounts = new Map<string, number>();
    executions.forEach(e => {
      const name = e.playbookName || 'Unknown';
      playbookCounts.set(name, (playbookCounts.get(name) || 0) + 1);
    });
    const topPlaybooks = Array.from(playbookCounts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
    
    return {
      totalExecutions,
      successRate,
      avgDuration,
      totalCost,
      activeExecutions,
      errorRate,
      avgTokensPerExecution,
      topPlaybooks
    };
  }, [executions, totalRecords]);

  // Fetch supporting data
  useEffect(() => {
    const fetchSupportingData = async () => {
      try {
        const [usersData, playbooksData] = await Promise.all([
          getUsers(),
          getPlaybooks(true)
        ]);
        
        const userMap = new Map();
        usersData.forEach((user: UserData) => {
          userMap.set(user.userId, user.email);
        });
        setUserMap(userMap);
        setPlaybooks(playbooksData);
      } catch (error) {
        console.error('Error fetching supporting data:', error);
      }
    };

    fetchSupportingData();
  }, []);

  // Fetch execution records
  useEffect(() => {
    fetchExecutions();
  }, [currentPage, filters]);

  const fetchExecutions = async () => {
      setLoading(true);
      setError(null);
      
      try {
      const hasFilters = 
        (filters.status && filters.status !== 'all') ||
        (filters.playbookName && filters.playbookName !== 'all') ||
        (filters.ownerId && filters.ownerId !== '') ||
        filters.dateFrom ||
        filters.dateTo;
        
        const filterParams = hasFilters ? {
          playbookName: filters.playbookName && filters.playbookName !== 'all' ? filters.playbookName.trim() : undefined,
          ownerId: filters.ownerId.trim() || undefined,
        status: filters.status && filters.status !== 'all' ? filters.status : undefined,
        dateFrom: filters.dateFrom?.toISOString() || null,
        dateTo: filters.dateTo?.toISOString() || null
        } : undefined;
        
        const response = await getExecutionRecords('', pageSize, currentPage, filterParams);
      setExecutions(response.items);
        setTotalRecords(response.metadata.scannedCount);
        setTotalPages(Math.ceil(response.metadata.scannedCount / pageSize));
      } catch (err) {
        console.error('Error fetching execution records:', err);
        setError('Failed to load execution records. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchExecutions();
    setRefreshing(false);
  };

  const handleFilterChange = (field: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: 'all',
      playbookName: 'all',
      ownerId: '',
      dateFrom: null,
      dateTo: null,
    });
    setCurrentPage(1);
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'error':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'in_progress':
        return <Activity className="h-4 w-4 text-blue-500 animate-pulse" />;
      case 'kill_switch':
        return <StopCircle className="h-4 w-4 text-orange-500" />;
      default:
        return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <Badge className="bg-green-50 text-green-700 border-green-200 hover:bg-green-100">Success</Badge>;
      case 'error':
        return <Badge className="bg-red-50 text-red-700 border-red-200 hover:bg-red-100">Error</Badge>;
      case 'in_progress':
        return <Badge className="bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100">In Progress</Badge>;
      case 'kill_switch':
        return <Badge className="bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100">Terminated</Badge>;
      default:
        return <Badge variant="secondary">{status || 'Unknown'}</Badge>;
    }
  };

  const formatDuration = (seconds: number | string) => {
    const numSeconds = typeof seconds === 'string' ? parseFloat(seconds) : seconds;
    if (!numSeconds || isNaN(numSeconds) || numSeconds <= 0) return '0s';
    
    // Handle unreasonably large durations (more than 24 hours = likely data error)
    if (numSeconds > 86400) {
      console.warn(`Unusually large duration detected: ${numSeconds} seconds`);
      return `${Math.round(numSeconds / 86400)}d`;
    }
    
    if (numSeconds < 60) return `${Math.round(numSeconds)}s`;
    if (numSeconds < 3600) return `${Math.round(numSeconds / 60)}m`;
    return `${Math.round(numSeconds / 3600)}h`;
  };

  const formatCost = (cost: number | string) => {
    const numCost = typeof cost === 'string' ? parseFloat(cost) : cost;
    if (!numCost || isNaN(numCost)) return '$0.00';
    return `$${numCost.toFixed(4)}`;
  };

  const formatTokens = (tokens: number | string) => {
    const numTokens = typeof tokens === 'string' ? parseFloat(tokens) : tokens;
    if (!numTokens || isNaN(numTokens)) return '0';
    if (numTokens < 1000) return Math.round(numTokens).toString();
    if (numTokens < 1000000) return `${(numTokens / 1000).toFixed(1)}K`;
    return `${(numTokens / 1000000).toFixed(1)}M`;
  };

  const MetricsOverview = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
      <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-xs md:text-sm font-medium">Total Executions</p>
              <p className="text-2xl md:text-3xl font-bold text-blue-900">{metrics.totalExecutions}</p>
            </div>
            <PlayCircle className="h-6 w-6 md:h-8 md:w-8 text-blue-500" />
          </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-600 text-xs md:text-sm font-medium">Success Rate</p>
              <p className="text-2xl md:text-3xl font-bold text-green-900">{metrics.successRate.toFixed(1)}%</p>
            </div>
            <TrendingUp className="h-6 w-6 md:h-8 md:w-8 text-green-500" />
              </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-600 text-xs md:text-sm font-medium">Avg Duration</p>
              <p className="text-2xl md:text-3xl font-bold text-purple-900">{formatDuration(metrics.avgDuration)}</p>
                  </div>
            <Timer className="h-6 w-6 md:h-8 md:w-8 text-purple-500" />
              </div>
        </CardContent>
      </Card>

      <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-600 text-xs md:text-sm font-medium">Total Cost</p>
              <p className="text-2xl md:text-3xl font-bold text-orange-900">{formatCost(metrics.totalCost)}</p>
              </div>
            <Database className="h-6 w-6 md:h-8 md:w-8 text-orange-500" />
          </div>
        </CardContent>
      </Card>
      </div>
    );

  const BasicFilters = () => (
    <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center">
        {/* Status Filter */}
        <div className="w-full sm:w-48">
          <Select value={filters.status} onValueChange={(value) => handleFilterChange('status', value)}>
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="success">Success</SelectItem>
              <SelectItem value="error">Error</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="kill_switch">Terminated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Playbook Filter */}
        <div className="w-full sm:w-64">
          <Select 
            value={filters.playbookName} 
            open={playbookDropdownOpen}
            onOpenChange={(open) => {
              setPlaybookDropdownOpen(open);
              if (!open) {
                setPlaybookSearchTerm(''); // Clear search when dropdown closes
              }
            }}
            onValueChange={(value) => {
              handleFilterChange('playbookName', value);
              setPlaybookSearchTerm(''); // Clear search when selecting
              setPlaybookDropdownOpen(false); // Close dropdown when selecting
            }}
          >
            <SelectTrigger className="text-sm">
              <SelectValue placeholder="All playbooks" className="truncate" />
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {playbooks.length > 5 && (
                <div 
                  className="p-2 border-b sticky top-0 bg-white z-10"
                  onPointerDown={(e) => e.preventDefault()}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Search playbooks..."
                      value={playbookSearchTerm}
                      onChange={(e) => {
                        e.stopPropagation();
                        setPlaybookSearchTerm(e.target.value);
                        // Keep dropdown open when typing
                        if (!playbookDropdownOpen) {
                          setPlaybookDropdownOpen(true);
                        }
                      }}
                      className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      onPointerDown={(e) => e.stopPropagation()}
                      onMouseDown={(e) => e.stopPropagation()}
                      onKeyDown={(e) => e.stopPropagation()}
                      onInput={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      autoComplete="off"
                    />
                  </div>
                </div>
              )}
              <SelectItem value="all">All Playbooks</SelectItem>
              {filteredPlaybooks.length > 0 ? (
                filteredPlaybooks.map((playbook) => (
                  <SelectItem key={playbook.playbookId} value={playbook.playbookName}>
                    <div className="truncate max-w-[200px]" title={playbook.playbookName}>
                      {playbook.playbookName}
                    </div>
                  </SelectItem>
                ))
              ) : (
                playbookSearchTerm && (
                  <div className="p-2 text-sm text-gray-500 text-center">
                    No playbooks found matching "{playbookSearchTerm}"
                  </div>
                )
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        <Button 
          variant="outline" 
          size="sm" 
          onClick={clearFilters}
          className="text-gray-600 text-sm whitespace-nowrap"
        >
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>
    </div>
  );

  const ExecutionCard = ({ execution }: { execution: ExecutionRecord }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
      <Card 
        className="hover:shadow-md transition-shadow border-l-4 border-l-blue-500 cursor-pointer"
        onClick={() => navigate(`/studio/executions/${execution.executionId}`)}
      >
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
              {getStatusIcon(execution.status || '')}
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-gray-900 text-sm md:text-base truncate">{execution.playbookName || 'Unknown Playbook'}</h3>
                <p className="text-xs md:text-sm text-gray-500 truncate">{execution.executionId}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {getStatusBadge(execution.status || '')}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                className="p-1 md:p-2"
              >
                {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-4 mb-4">
            <div className="flex items-center gap-1 md:gap-2">
              <Calendar className="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs md:text-sm text-gray-600 truncate">
                {new Date(execution.executionDate).toLocaleDateString()}
              </span>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <Timer className="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs md:text-sm text-gray-600">
                {formatDuration(execution.duration || 0)}
              </span>
            </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <Zap className="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs md:text-sm text-gray-600">
                {formatTokens(execution.totalTokens || 0)}
                </span>
              </div>
            
            <div className="flex items-center gap-1 md:gap-2">
              <Database className="h-3 w-3 md:h-4 md:w-4 text-gray-400 flex-shrink-0" />
              <span className="text-xs md:text-sm text-gray-600">
                {formatCost(execution.estimatedCost || 0)}
                </span>
              </div>
          </div>

          {expanded && (
            <div className="border-t pt-4 space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {execution.agentsCount && (
                  <div className="flex items-center gap-2">
                    <Bot className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{execution.agentsCount} agents</span>
            </div>
          )}

                {execution.toolsCount && (
                  <div className="flex items-center gap-2">
                    <Wrench className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{execution.toolsCount} tools</span>
              </div>
            )}
            
                {execution.conversationCount && (
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-gray-400" />
                    <span className="text-sm text-gray-600">{execution.conversationCount} conversations</span>
                  </div>
                )}
                
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-400" />
                  <span className="text-sm text-gray-600 truncate">
                    {userMap.get(execution.ownerId || '') || execution.ownerId || 'Unknown'}
                  </span>
                </div>
          </div>

              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/studio/executions/${execution.executionId}`);
                  }}
                  className="flex items-center gap-2 text-sm"
                >
                  <Eye className="h-4 w-4" />
                  View Details
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  if (loading && executions.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <div className="text-center">
          <Spinner />
          <p className="mt-4 text-gray-600">Loading execution records...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-3 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 md:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4">
              <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Execution Dashboard</h1>
              <p className="text-gray-600 mt-1 text-sm md:text-base">Monitor and analyze your playbook executions</p>
                  </div>
            <div className="flex items-center gap-2 md:gap-3">
                <Button 
                  variant="outline" 
                onClick={handleRefresh}
                disabled={refreshing}
                className="flex items-center gap-2 text-sm"
                  size="sm" 
              >
                <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Refresh</span>
                </Button>
                </div>
              </div>

          {/* View Mode Toggle - Mobile Responsive */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex items-center gap-2">
              <Label htmlFor="view-mode" className="text-sm">View:</Label>
              <Select value={viewMode} onValueChange={(value: any) => setViewMode(value)}>
                <SelectTrigger className="w-28 md:w-32 text-sm">
                  <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                  <SelectItem value="metrics">Metrics</SelectItem>
                  <SelectItem value="cards">Cards</SelectItem>
                  <SelectItem value="table">Table</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
              </div>
                </div>

        {/* Metrics Overview */}
        {viewMode === 'metrics' && <MetricsOverview />}

        {/* Filters */}
        <BasicFilters />

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-700">
                <XCircle className="h-5 w-5 flex-shrink-0" />
                <p className="text-sm">{error}</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Results Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">
              Execution Records ({totalRecords})
            </h2>
            {metrics.activeExecutions > 0 && (
              <Badge className="bg-blue-50 text-blue-700 border-blue-200 w-fit">
                {metrics.activeExecutions} Active
                                  </Badge>
                                )}
                              </div>
          
          <div className="flex items-center gap-2">
            <span className="text-xs md:text-sm text-gray-600">
              Page {currentPage} of {totalPages}
                      </span>
                    <Button
                      variant="outline"
                      size="sm"
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
              className="text-xs md:text-sm"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
              className="text-xs md:text-sm"
                    >
                      Next
                    </Button>
                  </div>
                </div>

        {/* Execution List */}
        {executions.length === 0 ? (
          <Card>
            <CardContent className="p-8 md:p-12 text-center">
              <FileText className="h-8 w-8 md:h-12 md:w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">No executions found</h3>
              <p className="text-gray-600 text-sm md:text-base">Try adjusting your filters or create a new playbook execution.</p>
          </CardContent>
        </Card>
        ) : (
          <div className="space-y-3 md:space-y-4">
            {executions.map((execution) => (
              <ExecutionCard key={execution.executionId} execution={execution} />
            ))}
          </div>
        )}


      </div>
    </div>
  );
};

export default AllExecutionsPage; 